import React, { useState, useEffect } from 'react';
import useInView from '../hooks/useInView';
import { PhoneCall, Mic, FileText, BarChart2, Users, Zap } from 'lucide-react';

/* The hero canvas, lifted out of Hero.jsx so more than one page can stand on
   it. Nothing here is page-specific: the caller supplies the copy column and
   whatever sits beside it, and this owns the brand plane, the agent mesh, the
   waves and the in-view reveal. Home and /features share it, which is what
   keeps the two from drifting apart the next time either is touched. */

/* Filled person mark. lucide's User is a stroke glyph, which vanishes at
   30px on a saturated plane — a solid shape holds at this size. */
const PersonGlyph = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="12" cy="8" r="4.2" />
    <path d="M12 14.2c-4.5 0-8 2.7-8 6V21h16v-0.8c0-3.3-3.5-6-8-6Z" />
  </svg>
);

/* ── The agent mesh on the brand plane ────────────────────────────────────
   Coordinates are the bubble's CENTRE (the element is translated -50%),
   panel-relative, and hand-scattered rather than Math.random()'d: a runtime
   roll would reshuffle on every render and could not be reviewed or
   screenshot-tested. Each one clears the curve at its own height — the mask
   runs from x=30 at the top in to x=8 around y=46 and back out to x=26 — so
   a bubble is trimmed by the edge at most, never orphaned onto the white.

   Sizes sit in a tight 30-40px band: enough variation to break the grid, not
   so much that one reads as a different component. */
const BUBBLE_SEEDS = [
  [22, 8, 34], [18, 19, 40], [46, 5, 30],  [62, 11, 38], [91, 22, 36],
  [88, 38, 33], [12, 52, 37], [17, 68, 35], [84, 72, 39], [72, 88, 34],
  [80, 7, 31],  [14, 34, 32], [93, 55, 30], [28, 84, 31], [48, 93, 32],
];

/* The panel is roughly 728 x 830, so one percent of its width is a shorter
   step than one percent of its height. Comparing raw percentages would
   over-weight horizontal distance and pick the wrong neighbours. */
const PANEL_ASPECT = 0.88;
const CYCLE = 6;        // seconds; bubbles and links share it
const NEIGHBOURS = 3;   // links each bubble reaches for, before de-duplication

/* Delay is a function of POSITION rather than an arbitrary number per bubble.
   That is what makes the graph possible: neighbours end up sharing almost the
   same phase, so an edge between them has a window where both ends are on
   screen.

   The coefficients are deliberately shallow. They set how far apart two
   neighbours can drift in phase, and that gap is subtracted directly from
   how long an edge can be drawn: a bubble is up from 7% to 52% of the cycle,
   so an edge between two of them only fits in 7% .. (52% - drift). At the
   steeper wave this file started with, drift hit 21% and edges survived barely
   a second. At 0.02/0.006 drift is 12%, which leaves a 7-40% window while
   still spreading the pop over 1.9s. Total stays inside the 6s cycle, so
   nothing wraps and Math.max below is always the genuinely later one. */
const delayOf = (x, y) => +(y * 0.02 + x * 0.006).toFixed(2);

const AGENT_BUBBLES = BUBBLE_SEEDS.map(([x, y, size]) => ({
  x, y,
  size: `${size}px`,
  left: `${x}%`,
  top: `${y}%`,
  delay: `${delayOf(x, y)}s`,
}));

const gapBetween = (a, b) => Math.hypot((a.x - b.x) * PANEL_ASPECT, a.y - b.y);

/* Every bubble reaches for its nearest few, and duplicates collapse — a
   mutually-nearest pair would otherwise be drawn twice, at double opacity.
   At NEIGHBOURS = 3 every bubble ends up with three or four edges radiating
   from it, which is what makes it read as a graph rather than as a chain of
   pairs; 27 edges in total. Longest is 37.8 units, and none of them passes
   through the phone's footprint (x 34-67, y 24-82) — checked, not assumed, so
   no length cutoff is needed.

   `head` is whichever end appears FIRST. The edge is drawn from there, so it
   grows outward from a bubble that is already on screen instead of arriving
   from empty space. `delay` is the LATER end's, so the edge cannot start
   before both of its bubbles exist. */
const AGENT_LINKS = (() => {
  const edges = new Map();
  AGENT_BUBBLES.forEach((from, i) => {
    AGENT_BUBBLES
      .map((to, j) => ({ j, to, d: gapBetween(from, to) }))
      .filter(o => o.j !== i)
      .sort((a, b) => a.d - b.d)
      .slice(0, NEIGHBOURS)
      .forEach(({ j, to }) => {
        const key = i < j ? `${i}-${j}` : `${j}-${i}`;
        if (edges.has(key)) return;
        const fromDelay = parseFloat(from.delay);
        const toDelay = parseFloat(to.delay);
        const [head, tail] = fromDelay <= toDelay ? [from, to] : [to, from];
        edges.set(key, {
          key,
          from: head,
          to: tail,
          delay: `${Math.max(fromDelay, toDelay)}s`,
        });
      });
  });
  return [...edges.values()];
})();

const DIALER_STEPS = ['Calling…', 'Connected', 'Recording', 'Summarising'];

/**
 * The phone mockup sits on the media stage — the one token set that does not
 * flip with the theme, because a light letterbox blows out a screen mock the
 * same way it blows out video.
 */
export const PhoneMockup = () => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setStep(s => (s + 1) % DIALER_STEPS.length), 2200);
    return () => clearInterval(t);
  }, []);

  const isLive = step > 0;

  return (
    <div className="relative mx-auto w-[240px]">
      <div className="relative h-[480px] w-[240px] overflow-hidden rounded-[2.5rem] border-4 border-stage-tile bg-stage shadow-lift">
        <div className="absolute left-1/2 top-3 z-10 h-5 w-20 -translate-x-1/2 rounded-pill bg-stage" />

        <div className="flex h-full flex-col px-4 pb-6 pt-11">
          <div className="mb-6 flex justify-between px-1 font-mono text-[10px] text-stage-muted">
            <span>09:41</span>
            <span>4G ▮▮</span>
          </div>

          <div className="mb-6 text-center text-2xs font-bold uppercase tracking-[0.18em] text-stage-muted">
            WASLX Dialer
          </div>

          <div className="mb-6 flex flex-col items-center">
            <div className={`relative mb-4 ${isLive ? 'ring-animate' : ''}`}>
              <div className="flex h-16 w-16 items-center justify-center rounded-pill bg-primary text-xl font-bold text-on-primary">
                WD
              </div>
            </div>
            <div className="text-sm font-bold text-stage-on">Waslx Demo</div>
            <div className="mt-0.5 font-mono text-xs text-stage-muted">+91 77000 18187</div>
          </div>

          <div className="mb-5 flex justify-center">
            <div
              className={`inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 text-xs transition-colors duration-500 ${
                isLive ? 'bg-primary-tint text-primary-strong' : 'bg-stage-tile text-stage-muted'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-pill ${isLive ? 'animate-pulse-soft bg-success' : 'bg-stage-muted'}`} />
              {DIALER_STEPS[step]}
            </div>
          </div>

          {isLive && (
            <div className="mb-5 grid grid-cols-2 gap-2">
              {[{ label: 'Duration', value: '0:47' }, { label: 'Quality', value: 'HD' }].map(s => (
                <div key={s.label} className="rounded-md bg-stage-tile p-2.5 text-center">
                  <div className="font-mono text-sm font-bold text-stage-on">{s.value}</div>
                  <div className="mt-0.5 text-[10px] text-stage-muted">{s.label}</div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-auto flex items-center justify-center gap-5">
            <button
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              className="flex h-10 w-10 items-center justify-center rounded-pill bg-stage-tile text-stage-muted"
            >
              <Mic className="h-4 w-4" />
            </button>
            <button
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              className={`flex h-14 w-14 items-center justify-center rounded-pill text-on-primary transition-colors duration-500 ${
                isLive ? 'bg-danger' : 'bg-primary'
              }`}
            >
              <PhoneCall className="h-6 w-6" />
            </button>
            <button
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              className="flex h-10 w-10 items-center justify-center rounded-pill bg-stage-tile text-stage-muted"
            >
              <FileText className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Floating stat cards — each on its own hue, per the multicolour rule.
          Anchored with right-full / left-full rather than a fixed -left-16:
          the offset has to be measured from the card's own width, which
          differs per label, or the widest one lands on the phone screen and
          covers the status bar. A negative margin then pulls back the exact
          overlap wanted. w-max is required with it: right:100% leaves zero
          available width, so shrink-to-fit collapses the card to min-content
          and every label wraps one word per line.

          lg-only, like the panel they sit on. Below that the phone centres
          on bare surface and a card hung off its edge would be half-clipped
          by .hero-canvas's overflow. */}
      {[
        { cls: 'right-full -mr-6 top-14 accent-1',    icon: BarChart2, value: '78%',     label: 'Connect rate' },
        { cls: 'left-full -ml-6 top-40 accent-4',     icon: Users,     value: '24 live', label: 'Agents active' },
        { cls: 'right-full -mr-4 bottom-16 accent-3', icon: Zap,       value: 'Auto',    label: 'Dialing' },
      ].map(({ cls, icon: Icon, value, label }, i) => (
        <div
          key={label}
          className={`absolute ${cls} hidden w-max animate-fade-in rounded-xs bg-surface px-3 py-2.5 shadow-soft lg:block`}
          style={{ animationDelay: `${240 + i * 160}ms` }}
        >
          <div className="flex items-center gap-2.5">
            <span className="icon-tile icon-tile--sm">
              <Icon className="h-4 w-4" />
            </span>
            <div>
              <div className="text-sm font-bold text-content">{value}</div>
              <div className="text-2xs text-content-muted">{label}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * The brand plane sits behind the phone, not behind the copy: the
 * headline keeps --color-text on --color-surface, which is the only
 * arrangement where both halves stay legible. Every layer is CSS —
 * see .hero-canvas in design-system.css.
 */

/* The section shell. `children` land inside the standard container, so a
   caller lays out its own grid exactly as the home hero always did. */
export const HeroCanvas = ({ children }) => {
  const [ref, inView] = useInView();

  return (
  <section ref={ref} className={`hero-canvas reveal bg-surface pt-[72px] ${inView ? 'is-inview' : ''}`}>
    <span className="hero-canvas__halo reveal-bg" aria-hidden="true" />
    <span className="hero-canvas__panel reveal-bg" aria-hidden="true">
      {/* Links first, so they pass behind the bubbles they join. Percentage
          endpoints resolve against the SVG's own box, which is the panel —
          the same coordinate space the bubbles are placed in. */}
      <svg className="hero-links">
        {AGENT_LINKS.map(({ key, from, to, delay }) => (
          <line
            key={key}
            x1={from.left} y1={from.top} x2={to.left} y2={to.top}
            pathLength="1"
            className="hero-link"
            style={{ '--link-delay': delay }}
          />
        ))}
      </svg>

      {AGENT_BUBBLES.map(b => (
        <span
          key={`${b.left}${b.top}`}
          className="hero-bubble"
          style={{
            left: b.left,
            top: b.top,
            '--bubble-size': b.size,
            '--bubble-delay': b.delay,
          }}
        >
          <PersonGlyph />
        </span>
      ))}
    </span>

    <span className="hero-canvas__waves reveal-bg" aria-hidden="true" />

    <div className="container-page py-16 lg:py-24">
      {children}
    </div>
  </section>
  );
};
